import { ErrorBoundary } from '@sentry/react';
import { LibraryCache } from 'base/library_cache';
import { ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import { useDocumentTitle } from 'base/ui/document_title';
import { Rows } from 'base/ui/layout';
import { TextSmall } from 'base/ui/typography';
import { isReplaceableTextNode } from 'base/webflow/replaceable_content';
import { Component, componentSchema } from 'base/webflow/schema';
import { zodParse } from 'base/zod_parse';
import { DEFAULT_FEATURE_FLAGS } from 'blocks/blocks_service';
import { EditorMode } from 'blocks/editor/editor_mode';
import {
  anElementInstance,
  anInlineSection,
  aProject,
  ElementSourceState,
  projectDomain,
} from 'blocks/editor/model';
import { SelectionFrames } from 'blocks/editor/selection/frame';
import { SelectionStoreImpl } from 'blocks/editor/selection/store';
import { SelectionActions } from 'blocks/editor/selection_actions';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import {
  EnumHashParameterStore,
  StringHashParameterStore,
} from 'blocks/hash_parameter';
import { PresenceDecorations } from 'blocks/sync/presence_decorations';
import { SimpleConnection } from 'blocks/woot_woot/base';
import * as mobx from 'mobx';
import { observer } from 'mobx-react-lite';
import { computedFn, fromPromise } from 'mobx-utils';
import React from 'react';
import { NoSlotsComponentView } from './component_renderer';
import {
  AddEffect,
  baseDecorator,
  composeDecorators,
  makeDropdownDecorator,
  makeFormVisibilityDecorator,
  makeNavbarDecorator,
  makeSliderDecorator,
  makeTabsDecorator,
  NodeDecorator,
} from './decorators';
import { measureTextLines, TextLineMeasurements } from './text_measuring';
import { HIGHLIGHT_TEXT_EDITING_OVERLAYS_CLASSNAME } from './text_overrides';

const nameParam = new StringHashParameterStore('component');
const viewportParam = new EnumHashParameterStore(
  'viewport',
  ['desktop', 'tablet', 'mobile'],
  'desktop',
);

const playgroundFlags = new FeatureFlagsStore(DEFAULT_FEATURE_FLAGS);

export function installComponentRenderingPlayground(
  library: LibraryCache,
): React.ComponentType {
  const resultsBySearch = computedFn(
    (search: string) =>
      fromPromise(
        library.findPublicComponents({
          search,
        }),
      ),
    { keepAlive: true },
  );

  const visibility = new VisibilityStore();

  const ComponentPlayground = observer(function _ComponentPlayground({
    name,
    component,
  }: {
    name: string;
    component: Component;
  }) {
    const selection = React.useMemo(() => new FakeSelection(), []);
    const textMeasurements = React.useMemo(
      () => mobx.observable.map<string, TextLineMeasurements>(),
      [],
    );
    const decorator = React.useMemo<NodeDecorator>(
      () =>
        composeDecorators(
          baseDecorator,
          makeDropdownDecorator(visibility),
          makeNavbarDecorator(visibility),
          makeFormVisibilityDecorator(visibility),
          makeSliderDecorator(visibility),
          makeTabsDecorator(visibility),
          makeTextMeasuringDecorator(textMeasurements),
        ),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const iframeUrl = `https://apis.relume.io/v1/preview/${name}/`;
    const viewport = viewportParam.get();
    const innerWidth =
      viewport === 'desktop' ? 1440 : viewport === 'tablet' ? 810 : 375;
    const innerViewHeight =
      viewport === 'desktop' ? 800 : viewport === 'tablet' ? 880 : 667;
    const outerWidth =
      viewport === 'desktop' ? 500 : viewport === 'tablet' ? 500 : 375;

    return (
      <>
        <ButtonPalette isDarker>
          <RadioButtonGroup
            value={viewport}
            onChange={(v) => viewportParam.set(v)}
            options={
              [
                { label: 'Desktop', value: 'desktop' },
                { label: 'Tablet', value: 'tablet' },
                { label: 'Mobile', value: 'mobile' },
              ] as const
            }
          />
        </ButtonPalette>
        <VisibilityToggles visibility={visibility} />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${outerWidth}px ${outerWidth}px`,
            height: innerViewHeight * (outerWidth / innerWidth) + 64,
            gridGap: 32,
          }}
        >
          <div className={HIGHLIGHT_TEXT_EDITING_OVERLAYS_CLASSNAME}>
            <b>Internal renderer:</b>
            <ErrorBoundary
              fallback={<TextSmall>Error rendering component</TextSmall>}
            >
              <div style={{ background: 'red' }}>
                <NoSlotsComponentView
                  component={component}
                  outerWidth={outerWidth}
                  innerWidth={innerWidth}
                  innerViewHeight={innerViewHeight}
                  decorator={decorator}
                />
              </div>
            </ErrorBoundary>
          </div>
          <div>
            <b>
              <a href={iframeUrl} rel="noreferrer" target="_blank">
                Webflow rendered (iframe):
              </a>
            </b>
            <iframe
              key={iframeUrl}
              src={iframeUrl}
              style={{
                width: innerWidth,
                height: innerViewHeight,
                transform: `scale(${outerWidth / innerWidth})`,
                transformOrigin: '0 0',
              }}
            />
          </div>
        </div>
        <b>Text measurements:</b>
        <code style={{ whiteSpace: 'pre-wrap', userSelect: 'text' }}>
          {[...textMeasurements.entries()].map(([k, v]) => (
            <div key={k}>
              <i>{k}</i>: {v.lineCount} lines
              <br />
              <LineBoxes boxes={v.lineBoxes} />
              <br />
              <br />
            </div>
          ))}
        </code>
        <b>JSON:</b>
        <code style={{ whiteSpace: 'pre-wrap', userSelect: 'text' }}>
          {JSON.stringify(component, null, 2)}
        </code>

        <SelectionFrames
          store={selection.store}
          flags={playgroundFlags}
          library={library}
        />
      </>
    );
  });

  return observer(() => {
    const [search, setSearch] = React.useState('');
    const name =
      nameParam.get() ||
      'feature-sections-left-1-2-col-part-2/section_layout105';

    const [title, raw] = useLoadComponent(name, library);
    useDocumentTitle(`${title} - Component renderer playground`);

    return (
      <Rows spacing={16}>
        <div>
          Search for a component name:{' '}
          <input value={search} onChange={(e) => setSearch(e.target.value)} />
          {resultsBySearch(search).case<React.ReactNode>({
            pending: () => 'Loading',
            rejected: (e) => `Error: ${e}`,
            fulfilled: (results) =>
              results.components.map((r) => {
                return (
                  <button
                    key={r.componentSlug}
                    onClick={() =>
                      nameParam.set(`${r.categorySlug}/${r.componentSlug}`)
                    }
                  >
                    {r.name}
                  </button>
                );
              }),
          })}
        </div>
        <div>
          Component name or https json url:{' '}
          <input
            value={name}
            onChange={(e) => nameParam.set(e.target.value)}
            size={50}
          />
          <ComponentNameEditNumberButtons
            value={name}
            onChange={nameParam.set}
          />
        </div>
        {raw == null && <TextSmall>No component</TextSmall>}
        {raw?.type === 'pending' && <TextSmall>Loading...</TextSmall>}
        {raw?.type === 'rejected' && (
          <TextSmall>Error: {`${raw.error}`}</TextSmall>
        )}
        {raw?.type === 'fulfilled' && (
          <ComponentPlayground
            name={name}
            component={zodParse(componentSchema, raw.value)}
          />
        )}
      </Rows>
    );
  });
}

function useLoadComponent(cname: string, library: LibraryCache) {
  const [httpData, setHttpData] = React.useState<
    | null
    | { type: 'pending' }
    | { type: 'rejected'; error: unknown }
    | { type: 'fulfilled'; value: unknown }
  >(null);
  React.useMemo(() => {
    const abort = new AbortController();
    if (cname.startsWith('https://')) {
      fetch(cname, { signal: abort.signal })
        .then((r) => r.json())
        .then((r) => {
          if (abort.signal.aborted) return;
          setHttpData({ type: 'fulfilled', value: r });
        })
        .catch((e) => {
          if (abort.signal.aborted) return;
          setHttpData({ type: 'rejected', error: e });
        });
    } else {
      setHttpData(null);
    }
    return () => abort.abort();
  }, [cname]);

  const [_, componentSlug = ''] = cname.split('/');
  const componentRaw = cname.startsWith('https://')
    ? null
    : library.getCachedPublicComponentJson(componentSlug, {
        fetchIfMissing: true,
      });
  return [componentSlug, httpData || componentRaw] as const;
}

const makeTextMeasuringDecorator = (
  store: mobx.ObservableMap<string, TextLineMeasurements>,
): NodeDecorator => {
  const measureEffect: AddEffect = (node, _ctx, el) => {
    store.set(node._id, measureTextLines(el));
    return () => {};
  };

  return function* (node) {
    const replacementType = isReplaceableTextNode(node);
    if (replacementType == null) return;
    yield { addEffect: measureEffect };
  };
};

function LineBoxes({ boxes }: { boxes: readonly DOMRect[] }) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const colors = ['#f00', '#0f0', '#00f', '#ff0', '#f0f', '#0ff'];
  const rects = boxes.map((box, i) => {
    minX = Math.min(minX, box.x);
    maxX = Math.max(maxX, box.x + box.width);
    minY = Math.min(minY, box.y);
    maxY = Math.max(maxY, box.y + box.height);
    const isThin = box.width < 1;
    return (
      <rect
        key={i}
        x={box.x}
        y={box.y}
        width={isThin ? 5 : box.width}
        height={box.height}
        fill={isThin ? '#000' : colors[i % colors.length]}
        fillOpacity={0.5}
        rx={isThin ? box.height : 0}
        ry={isThin ? box.height : 0}
        stroke="#000"
        strokeWidth={0.25}
      />
    );
  });
  return (
    <svg
      viewBox={`${minX - 1} ${minY - 1} ${maxX - minX + 2} ${maxY - minY + 2}`}
      style={{ border: '1px solid grey', maxWidth: 400, maxHeight: 400 }}
    >
      {rects}
    </svg>
  );
}

class VisibilityStore {
  showFormFields = true;
  showFormSuccess = false;
  showFormError = false;
  openMenus = false;
  sliderIndex = 0;
  tabIndex = 0;

  constructor() {
    mobx.makeAutoObservable(this);
  }
}

const VisibilityToggles = observer(function _VisibilityToggles({
  visibility,
}: {
  visibility: VisibilityStore;
}) {
  const checks = [
    ['showFormFields', 'Show form fields'],
    ['showFormSuccess', 'Show form success'],
    ['showFormError', 'Show form error'],
    ['openMenus', 'Open menus'],
  ] as const;
  const numbers = [
    ['sliderIndex', 'Slider index'],
    ['tabIndex', 'Tab index'],
  ] as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {checks.map(([k, label]) => (
        <label key={k}>
          <input
            type="checkbox"
            checked={visibility[k]}
            onChange={(e) => (visibility[k] = e.target.checked)}
          />
          {label}
        </label>
      ))}
      {numbers.map(([k, label]) => (
        <label key={k}>
          {label}
          <input
            type="number"
            min={0}
            max={10}
            value={visibility[k]}
            onChange={(e) => (visibility[k] = parseInt(e.target.value, 10))}
          />
        </label>
      ))}
    </div>
  );
});

function ComponentNameEditNumberButtons({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const numberString = value.match(/(\d+)[^\d]*$/)?.[1];
  if (numberString == null) return null;
  const number = parseInt(numberString, 10);
  if (isNaN(number)) return null;

  return (
    <>
      <button
        onClick={() =>
          onChange(value.replace(numberString, (number + 1).toString()))
        }
      >
        +1
      </button>
      {number > 1 && (
        <button
          onClick={() =>
            onChange(value.replace(numberString, (number - 1).toString()))
          }
        >
          -1
        </button>
      )}
    </>
  );
}

export class FakeSelection {
  constructor(
    private readonly initialElementSource: ElementSourceState = {
      slug: 'abc123',
    },
  ) {}

  readonly project = projectDomain.create(
    'P',
    aProject({}),
    new SimpleConnection(),
  );
  readonly section = this.project.homePage.sections.push(
    anInlineSection({
      name: 'Test section',
      element: {
        current: anElementInstance({ source: this.initialElementSource }),
      },
    }),
  )[0];
  readonly elementInstance = (() => {
    if (this.section.type === 'reference') throw new Error();
    return this.section.value.element.current!;
  })();

  readonly myDecorations = mobx.observable.map<string, string | undefined>();

  readonly actions: SelectionActions = {
    showCopyExportOptions: false,
    maybePasteViaEvent: () => {},
  };

  readonly store = new SelectionStoreImpl(
    this.project,
    () => EditorMode.Wireframe,
    {
      ensureVisible: (el) => {
        console.info('Ensuring visible', el);
      },
    },
    { canViewWireframe: () => true },
    new PresenceDecorations({
      getMine: (key) => this.myDecorations.get(key),
      has: (key) => this.myDecorations.get(key) != null,
      set: (key, value) => this.myDecorations.set(key, value),
      getOthers: () => new Map(),
    }),
    true,
  );

  readonly makeState = computedFn((id: string) => ({
    ...this.store.factory.forSection(this.section),
    element: this.elementInstance,
    nodeId: id,
  }));
}
